import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-bootstrap/Modal';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Image from 'react-bootstrap/Image';
import Card from 'react-bootstrap/Card';
import Stack from 'react-bootstrap/Stack';
import { render } from 'storyblok-rich-text-react-renderer';
import { useSelector } from 'react-redux';
import UnderlyingCard from './UnderlyingCard';
import API from '../../services/API';
import i18next from '../../translations/i18n';

const filterUnderlyings = (assetClasses, underlyings) => {
  const flattenUnderlyingList = assetClasses.reduce((arr, elem) => {
    elem.groups.forEach((group) => {
      arr.push(...group.underlyingList);
    });
    return arr;
  }, []);
  return flattenUnderlyingList.filter((item) =>
    underlyings.includes(item.ticker),
  );
};

const getTextURL = (fileUrl) => {
  try {
    const url = new URL(fileUrl);
    return url.href;
  } catch {
    const { origin } = window.location;
    const { isoCode = 'en-GB' } = window.ttCentre;
    return `${origin}/${isoCode.toLowerCase()}/${fileUrl}`;
  }
};

const PostModal = (props) => {
  const appState = useSelector((state) => state.app);
  const { data: assetClasses } = appState;
  const { post, onHide, onEscapeKeyDown } = props;
  const {
    postTitle,
    postDescriptionText,
    externalFileLinks = [],
    chartImage = {},
    underlyings = [],
    source = '',
  } = post;
  const { filename, alt } = chartImage;
  const [openLayer, setOpenModal] = useState(false);
  const unerlyings = filterUnderlyings(assetClasses, underlyings);

  return (
    <Modal
      onHide={onHide}
      show
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      style={{ opacity: !openLayer ? 1 : 0 }}
      dialogClassName="modal-90w"
      onEscapeKeyDown={onEscapeKeyDown}
    >
      <Modal.Header closeButton className="border-0 p-5 pb-0" />
      <Modal.Body className="px-sm-3 mx-sm-3 px-md-4 mx-md-4 px-lg-5 mx-lg-5 pb-5 mb-5">
        <Row className="mb-4 content">
          <Card.Title as="h3" className="card-post-title">
            {postTitle}{' '}
          </Card.Title>
        </Row>
        <div className="">
          <Row className="p-0" direction="horizontal" gap={5}>
            <Col sm={12}>
              <div className="mb-2">{render(postDescriptionText)}</div>
              {filename && (
                <Image
                  src={filename.replace(
                    '//a.storyblok.com',
                    '//a2.storyblok.com',
                  )}
                  alt={alt || 'Chart image'}
                  className="chart-image p-2"
                  style={{
                    cursor: 'pointer',
                    maxWidth: '100%',
                    height: 'auto',
                  }}
                  crossOrigin="anonymous"
                />
              )}
              {source && (
                <div className="row mt-3">
                  <div className="col">
                    <Card.Text className="text-source">
                      {i18next.t('Source')} : {source}
                    </Card.Text>
                  </div>
                </div>
              )}
              {externalFileLinks.length > 0 &&
                externalFileLinks.map((pdf) => (
                  <div
                    key={pdf._uid}
                    className="align-middle download-container mt-2"
                  >
                    <Card.Link
                      className={`mt-auto text-blue text-decoration-none pdf-link-ttcentre post-file-link ${
                        pdf.pdfFileUrl.url.indexOf('.pdf') !== -1
                          ? 'pdf-link-ttc'
                          : 'post-file-link'
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        API.sendToGA(
                          getTextURL(pdf.pdfFileUrl.url),
                          'pdf downloads',
                        );
                        window.open(getTextURL(pdf.pdfFileUrl.url), '_blank');
                      }}
                      target="_blank"
                    >
                      {`${pdf.fileName}.${
                        pdf.pdfFileUrl.url.split('.').pop().split(/|\?/)[0]
                      }`}
                    </Card.Link>
                  </div>
                ))}
              <div className="row g-3">
                {unerlyings.map((underlying) => (
                  <div className="col p-3" key={underlying.ticker}>
                    <UnderlyingCard
                      underlying={underlying}
                      setOpenModal={setOpenModal}
                      postTitle={postTitle}
                    />
                  </div>
                ))}
              </div>
            </Col>

            {filename && (
              <Col
                md={12}
                lg={6}
                className="d-flex flex-wrap align-items-center"
              />
            )}
            <Col md={12} lg={filename ? 6 : 12} />
          </Row>
          <Stack className="p-0 mt-5 float-none" />
        </div>
      </Modal.Body>
    </Modal>
  );
};

PostModal.propTypes = {
  onHide: PropTypes.func.isRequired,
  onEscapeKeyDown: PropTypes.func.isRequired,
  post: PropTypes.shape({
    postCreatedDate: PropTypes.string.isRequired,
    postTitle: PropTypes.string.isRequired,
    postDescriptionText: PropTypes.shape({}).isRequired,
    externalFileLinks: PropTypes.arrayOf(PropTypes.shape([]).isRequired),
    underlyings: PropTypes.arrayOf(PropTypes.string).isRequired,
    chartImage: PropTypes.shape({
      filename: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
    }),
    source: PropTypes.string.isRequired,
  }).isRequired,
};

export default PostModal;
